<template>
    <div v-if="selectedPlan" class="payment-summary">
        <div class="summary-info">
            <div class="plan-info-summary">
                <div class="plan-info">
                    <p class="plan-fullname">
                        {{ `Venue ${selectedPlan.name} plan` }}
                    </p>
                    <div class="plan-monthly-prices">
                        <p :style="intervalStyle(selectedCycle, 'month')">
                            ${{ monthTotalPrice(selectedPlan.name) }}
                        </p>
                        <p :style="intervalStyle(selectedCycle, 'year')">
                            ${{ yearTotalPrice(selectedPlan.name) }}
                        </p>
                        <p>for</p>
                        <p>12 months</p>
                    </div>
                </div>
                <div class="plan-price-info">
                    <div class="total-prices">
                        <p
                            :style="intervalStyle(selectedCycle, 'month')"
                            class="month-total-price"
                        >
                            ${{ monthTotalPrice(selectedPlan.name) }}
                        </p>
                        <p
                            :style="intervalStyle(selectedCycle, 'year')"
                            class="year-total-price"
                        >
                            ${{ yearTotalPrice(selectedPlan.name) }}
                        </p>
                    </div>
                    <p v-if="selectedCycle.interval === 'month'" class="savings">
                        {{
                            `Save $${
                                monthTotalPrice(selectedPlan.name) -
                                yearTotalPrice(selectedPlan.name)
                            }`
                        }}
                    </p>
                    <p
                        v-if="selectedCycle.interval === 'year'"
                        :style="{
                            color: '#8b8b8b',
                            'border-color': '#8b8b8b'
                        }"
                        class="savings"
                    >
                        {{
                            `$${
                                monthTotalPrice(selectedPlan.name) -
                                yearTotalPrice(selectedPlan.name)
                            } Saved`
                        }}
                    </p>
                </div>
            </div>
        </div>
        <div class="subscription-desc">
            <div v-if="canTrial" class="sub-desc-wrapper">
                <p>
                    Your 30-day trial is 100% free. Reminder before trial ends. Cancel anytime.
                </p>
                <p>
                    {{
                        `${canTrial ? 'Trial' : 'Subscription'} starts: ${subscriptionDate(
                            'today'
                        )}. Ends: ${subscriptionDate('end')}. We'll charge $${
                            selectedCycle.interval === 'month'
                                ? monthPrice(selectedPlan.name)
                                : yearTotalPrice(selectedPlan.name)
                        } on ${subscriptionDate('charge')}.`
                    }}
                </p>
                <p>Auto-billing so you continue to enjoy {{ selectedPlan.name }} features.</p>
            </div>
            <div v-else class="sub-desc-wrapper">
                <p>
                    {{
                        `${canTrial ? 'Trial' : 'Subscription'} starts: ${subscriptionDate(
                            'today'
                        )}. Ends: ${subscriptionDate('end')}.`
                    }}
                </p>
                <p>
                    We auto-bill so you continue to enjoy {{ selectedPlan.name }} features.
                    Your next auto-bill payment will be
                    {{
                        ` $${
                            selectedCycle.interval === 'month'
                                ? monthPrice(selectedPlan.name)
                                : yearTotalPrice(selectedPlan.name)
                        } on ${subscriptionDate('charge')}.`
                    }}
                </p>
            </div>
        </div>
        <div class="divider"></div>
        <div class="summary-total">
            <p>Total Due</p>
            <p v-if="canTrial">$0</p>
            <p v-else-if="selectedCycle.interval === 'month'">
                {{ `$${monthPrice(selectedPlan.name)}` }}
            </p>
            <p v-else-if="selectedCycle.interval === 'year'">
                {{ `$${yearTotalPrice(selectedPlan.name)}` }}
            </p>
        </div>
    </div>
</template>
<script>
    import moment from 'moment'
    import { useSubscription } from '@/composables/features/subscriptions/useSubscriptions'
    export default {
        name: 'PaymentSummary',
        props: {
            role: {
                type: String,
                required: true
            }
        },
        setup(props, context) {
            const {
                canTrial,
                selectedPlan,
                selectedCycle,
                monthPrice,
                monthTotalPrice,
                yearPrice,
                yearTotalPrice
            } = useSubscription(props, context)

            return {
                canTrial,
                selectedCycle,
                selectedPlan,
                monthPrice,
                monthTotalPrice,
                yearPrice,
                yearTotalPrice
            }
        },
        computed: {
            intervalStyle: () => (cycle, interval) =>
                cycle.interval !== interval
                    ? {
                          color: '#8b8b8b',
                          'text-decoration-line': 'line-through'
                      }
                    : {},
            subscriptionDate: (self) => (type) => {
                const today = new Date()
                const interval = self.canTrial ? 'month' : self.selectedCycle.interval
                let days

                switch (type) {
                    case 'charge': {
                        if (interval === 'month') {
                            days = 30
                        } else {
                            days = 365
                        }
                        break
                    }
                    case 'end': {
                        if (interval === 'month') {
                            days = 29
                        } else {
                            days = 364
                        }
                        break
                    }
                    case 'start':
                    default:
                        days = 0
                }

                return moment(today).add(days, 'days').format('MMM DD, YYYY')
            }
        }
    }
</script>
<style lang="scss" scoped>
    p {
        margin-bottom: 0px;
    }
    button {
        border: none;
    }
    .payment-summary {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        gap: 24px;

        .summary-info {
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            gap: 24px;

            .plan-info-summary {
                width: 100%;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;

                .plan-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: flex-start;
                    gap: 8px;

                    .plan-fullname {
                        color: white;
                        font-family: 'Instrument Sans';
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 650;
                        line-height: 120%;
                    }
                    .plan-monthly-prices {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                        align-items: center;
                        gap: 6px;
                        color: #f5f5f5;
                        font-family: 'Instrument Sans';
                        font-size: 18px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 133%;
                    }
                }
                .plan-price-info {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    gap: 6px;

                    .total-prices {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                        align-items: center;
                        gap: 6px;

                        .month-total-price,
                        .year-total-price {
                            color: #f5f5f5;
                            font-family: 'Instrument Sans';
                            font-size: 18px;
                            font-style: normal;
                            font-weight: 500;
                            line-height: 133%;
                        }
                    }
                    .savings {
                        display: flex;
                        flex-direction: row;
                        justify-content: center;
                        align-items: center;
                        text-wrap: nowrap;
                        padding: 4px 14px;
                        border-radius: 15px;
                        border: 1px solid #cdeb09;
                        color: #cdeb09;
                        font-family: 'Instrument Sans';
                        font-size: 12px;
                        font-style: normal;
                        font-weight: 500;
                        line-height: 135%;
                        letter-spacing: 0.12px;
                    }
                }
            }
        }
        .subscription-desc {
            width: 100%;
            margin-top: -8px;

            .sub-desc-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 8px;

                p {
                    width: 100%;
                    color: #acacac;
                    font-family: 'Instrument Sans';
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: 125%;
                }
            }
        }
        .divider {
            width: 100%;
            height: 1px;
            background: #464646;
        }
        .summary-total {
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            color: #fff;
            font-family: 'Instrument Sans';
            font-size: 18px;
            font-style: normal;
            font-weight: 500;
            line-height: 133%;
        }
    }
</style>
